import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ShieldCheck, UserCheck, UserX, ScrollText, RefreshCw, Download } from "lucide-react";
import { API, authHeaders } from "./shared";

const SOURCE_LABELS = {
  booking_form_checkbox: "Booking form checkbox",
  sms_yes_reply: "Texted YES",
  sms_start_reply: "Texted START",
  sms_stop_reply: "Texted STOP",
  unknown: "Unknown",
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const StatCard = ({ icon: Icon, label, value, tone, testId }) => (
  <div data-testid={testId} className="border border-brand-border bg-brand-surface/50 p-4">
    <div className="flex items-center gap-2 font-sans text-xs text-brand-text-muted uppercase tracking-wider">
      <Icon size={13} className={tone} /> {label}
    </div>
    <p className={`font-serif text-2xl font-semibold mt-1 ${tone}`}>{value}</p>
  </div>
);

const ConsentTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [view, setView] = useState("opted_in");

  const exportCsv = async () => {
    setExporting(true);
    try {
      const r = await axios.get(`${API}/admin/consent-export`, { ...authHeaders(), responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sms-optin-list-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Opted-in list downloaded");
    } catch {
      toast.error("Export failed");
    } finally { setExporting(false); }
  };

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/admin/consent-dashboard`, authHeaders())
      .then((r) => setData(r.data))
      .catch(() => toast.error("Failed to load consent data"))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <p className="font-sans text-sm text-brand-text-muted">Loading consent data…</p>;

  const th = "font-sans text-[10px] uppercase tracking-wider text-brand-text-muted px-3 py-2.5 whitespace-nowrap";
  const td = "px-3 py-3 font-sans text-sm";

  return (
    <div data-testid="consent-tab">
      <div className="flex items-center justify-between mb-2">
        <h2 className="font-serif text-2xl text-brand-text flex items-center gap-2">
          <ShieldCheck size={20} className="text-brand-gold" /> SMS Consent Dashboard
        </h2>
        <div className="flex items-center gap-2">
          <button onClick={exportCsv} disabled={exporting} data-testid="consent-export-btn"
            className="flex items-center gap-1.5 font-sans text-[11px] px-3 py-1.5 bg-brand-gold text-black font-semibold hover:opacity-90 transition-opacity disabled:opacity-50">
            <Download size={12} /> {exporting ? "Exporting…" : "Export CSV"}
          </button>
          <button onClick={load} disabled={loading} data-testid="consent-refresh-btn"
            className="flex items-center gap-1.5 font-sans text-[11px] px-3 py-1.5 border border-brand-border text-brand-text-muted hover:border-brand-gold hover:text-brand-gold transition-colors disabled:opacity-50">
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>
      <p className="font-sans text-xs text-brand-text-muted mb-6">
        Who can legally receive marketing texts, who opted out, and the full audit trail (US carrier compliance).
      </p>

      <div className="grid grid-cols-3 gap-4 mb-8">
        <StatCard icon={UserCheck} label="Opted In" value={data.stats.opted_in} tone="text-green-400" testId="consent-stat-optedin" />
        <StatCard icon={UserX} label="Opted Out" value={data.stats.opted_out} tone="text-red-400" testId="consent-stat-optedout" />
        <StatCard icon={ScrollText} label="Audit Events" value={data.stats.events} tone="text-brand-gold" testId="consent-stat-events" />
      </div>

      <div className="flex gap-1 mb-4">
        {[["opted_in", `Opted-In List (${data.stats.opted_in})`], ["opted_out", `Opt-Outs (${data.stats.opted_out})`], ["history", "Consent History"]].map(([v, label]) => (
          <button key={v} onClick={() => setView(v)} data-testid={`consent-view-${v}`}
            className={`font-sans text-xs px-4 py-2 border transition-colors ${view === v ? "border-brand-gold text-brand-gold bg-brand-gold/10" : "border-brand-border text-brand-text-muted hover:border-brand-gold/50"}`}>
            {label}
          </button>
        ))}
      </div>

      <div className="overflow-x-auto border border-brand-border">
        {view === "opted_in" && (
          <table className="w-full text-left" data-testid="consent-optedin-table">
            <thead><tr className="bg-brand-surface border-b border-brand-border">
              <th className={th}>Phone</th><th className={th}>Name</th><th className={th}>How they opted in</th><th className={th}>Consent Date</th>
            </tr></thead>
            <tbody>
              {data.opted_in.map((r) => (
                <tr key={r.phone} className="border-b border-brand-border/40 hover:bg-brand-surface/50">
                  <td className={`${td} text-brand-text font-medium whitespace-nowrap`}>{r.phone}</td>
                  <td className={`${td} text-brand-text-muted`}>{r.name || "—"}</td>
                  <td className={`${td} text-xs text-green-400`}>{SOURCE_LABELS[r.source] || r.source}</td>
                  <td className={`${td} text-xs text-brand-text-muted whitespace-nowrap`}>{fmtDate(r.consented_at)}</td>
                </tr>
              ))}
              {data.opted_in.length === 0 && (
                <tr><td colSpan={4} className="px-3 py-8 text-center font-sans text-sm text-brand-text-muted">No opted-in clients yet. Consent comes from the booking-form checkbox or a YES/START text reply.</td></tr>
              )}
            </tbody>
          </table>
        )}

        {view === "opted_out" && (
          <table className="w-full text-left" data-testid="consent-optedout-table">
            <thead><tr className="bg-brand-surface border-b border-brand-border">
              <th className={th}>Phone</th><th className={th}>Opted Out At</th>
            </tr></thead>
            <tbody>
              {data.opted_out.map((r) => (
                <tr key={r.phone} className="border-b border-brand-border/40 hover:bg-brand-surface/50">
                  <td className={`${td} text-brand-text font-medium whitespace-nowrap`}>{r.phone}</td>
                  <td className={`${td} text-xs text-red-400 whitespace-nowrap`}>{fmtDate(r.opted_out_at)}</td>
                </tr>
              ))}
              {data.opted_out.length === 0 && (
                <tr><td colSpan={2} className="px-3 py-8 text-center font-sans text-sm text-brand-text-muted">Nobody has opted out. STOP replies land here automatically.</td></tr>
              )}
            </tbody>
          </table>
        )}

        {view === "history" && (
          <table className="w-full text-left" data-testid="consent-history-table">
            <thead><tr className="bg-brand-surface border-b border-brand-border">
              <th className={th}>Date</th><th className={th}>Phone</th><th className={th}>Action</th><th className={th}>Source</th><th className={th}>Response</th>
            </tr></thead>
            <tbody>
              {data.history.map((h) => (
                <tr key={h.id} className="border-b border-brand-border/40 hover:bg-brand-surface/50">
                  <td className={`${td} text-xs text-brand-text-muted whitespace-nowrap`}>{fmtDate(h.created_at)}</td>
                  <td className={`${td} text-brand-text whitespace-nowrap`}>{h.phone}</td>
                  <td className={td}>
                    <span className={`font-sans text-[10px] uppercase px-2 py-1 border ${h.action === "consent" ? "border-green-500/40 text-green-400 bg-green-500/10" : "border-red-500/40 text-red-400 bg-red-500/10"}`}>
                      {h.action === "consent" ? "Opt-In" : "Opt-Out"}
                    </span>
                  </td>
                  <td className={`${td} text-xs text-brand-text-muted`}>{SOURCE_LABELS[h.source] || h.source}</td>
                  <td className={`${td} text-xs text-brand-gold`}>{h.response}</td>
                </tr>
              ))}
              {data.history.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-8 text-center font-sans text-sm text-brand-text-muted">No consent events recorded yet.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default ConsentTab;
